import { useEffect, useState } from "react";
import { Phone, Mail, Users, ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import Seo from "../components/Seo";

export default function Referees() {
  const [people, setPeople] = useState([]);
  const [futbalnet, setFutbalnet] = useState("");
  useEffect(() => {
    api.get("/people").then((r) => setPeople(r.data)).catch(() => {});
    api.get("/contact-info").then((r) => r.data?.futbalnet_url && setFutbalnet(r.data.futbalnet_url)).catch(() => {});
  }, []);

  const referees = people.filter((p) => p.type === "referee");
  const committee = people.filter((p) => p.type === "committee");
  const delegates = people.filter((p) => p.type === "delegate");

  return (
    <div data-testid="referees-page">
      <Seo title="Rozhodcovia a delegáti" description="Zoznam rozhodcov, asistentov, vedenia Komisie rozhodcov a delegátov zväzu ObFZ Levice. Postup pre nových rozhodcov." />
      <PageHeader
        eyebrow="Komisia rozhodcov · Delegáti zväzu"
        title="Rozhodcovia a delegáti"
        description="Zoznam rozhodcov, asistentov, vedenia KR a delegátov ObFZ Levice. Postup pre nových rozhodcov a kontaktné údaje."
        breadcrumbs={[{ label: "Rozhodcovia a delegáti" }]}
      />

      {futbalnet && (
        <section className="bg-green-950 text-white border-b-2 border-ink-900">
          <div className="wrap py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="mono text-[11px] font-bold uppercase tracking-widest text-green-100 mb-1">Delegačný list</div>
              <div className="font-heading font-bold text-lg">Aktuálne obsadenie rozhodcov a delegátov</div>
            </div>
            <a href={futbalnet} target="_blank" rel="noopener noreferrer"
               className="btn btn-white flex-shrink-0" data-testid="referees-obsadenie-btn">
              Aktuálne obsadenie <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </section>
      )}

      <section className="section-pad bg-paper">
        <div className="wrap">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <h2 className="display-md mb-6 border-b-2 border-ink-900 pb-4 flex items-center gap-3">
                <Users className="w-6 h-6 text-green-700" /> Aktívni rozhodcovia
              </h2>

              <div className="bg-white border border-ink-100 overflow-hidden">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="w-12">#</th>
                      <th>Meno</th>
                      <th className="hidden md:table-cell">Funkcia</th>
                      <th className="hidden md:table-cell">Mesto / Klub</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referees.map((p, i) => (
                      <tr key={p.id} data-testid={`referee-${p.id}`}>
                        <td className="mono text-xs text-ink-400">{String(i + 1).padStart(2, "0")}</td>
                        <td className="font-semibold text-ink-900">{p.name}</td>
                        <td className="hidden md:table-cell mono text-[11px] uppercase tracking-wider text-ink-500">{p.role}</td>
                        <td className="hidden md:table-cell text-ink-500">{p.location}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="display-md mb-6 border-b-2 border-ink-900 pb-4">Vedenie KR</h3>
                <div className="bg-white border border-ink-100 p-6 space-y-4">
                  {committee.map((p) => (
                    <div key={p.id} className="border-b border-ink-100 pb-3 last:border-0 last:pb-0">
                      <div className="font-heading font-bold text-ink-900">{p.name}</div>
                      <div className="mono text-[10px] uppercase tracking-widest text-green-700 mt-0.5">{p.role}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-green-700 text-white p-7 relative overflow-hidden">
                <div className="absolute top-0 right-0 stripe-accent w-28 h-28 opacity-20" />
                <div className="eyebrow-light mb-3">Pre záujemcov</div>
                <h3 className="font-heading font-extrabold text-white text-2xl leading-tight mb-3">Postup pre nových rozhodcov</h3>
                <p className="text-white/80 text-sm mb-5">
                  Záujemcovia o výkon funkcie futbalového rozhodcu z levického okresu sa môžu kontaktovať na:
                </p>
                <ul className="space-y-3 border-t border-white/20 pt-4">
                  <li className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-green-100" />
                    <a href="tel:0903384831" className="font-mono text-white font-semibold">0903 384 831</a>
                  </li>
                  <li className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-green-100" />
                    <a href="tel:0917774084" className="font-mono text-white font-semibold">0917 774 084</a>
                  </li>
                  <li className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-green-100" />
                    <a href="mailto:kr@obfz-levice.sk" className="text-white font-semibold">kr@obfz-levice.sk</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-white border-t border-ink-100" data-testid="delegates-section">
        <div className="wrap">
          <h2 className="display-md mb-6 border-b-2 border-ink-900 pb-4 flex items-center gap-3">
            <Users className="w-6 h-6 text-green-700" /> Delegáti zväzu
          </h2>
          <div className="bg-white border border-ink-100 overflow-hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="w-12">#</th>
                  <th>Meno</th>
                  <th>Adresa</th>
                </tr>
              </thead>
              <tbody>
                {delegates.map((p, i) => (
                  <tr key={p.id} data-testid={`delegate-${p.id}`}>
                    <td className="mono text-xs text-ink-400">{String(i + 1).padStart(2, "0")}</td>
                    <td className="font-semibold text-ink-900">{p.name}</td>
                    <td className="text-ink-500">{p.location}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
